/**
 * Field-metadata cache.
 *
 * Field lists are needed by nearly every widget (to build correctly-typed
 * filter expressions) and by every configuration form. Without a cache, a
 * dashboard with ten widgets on one layer would issue ten identical
 * DescribeFeatureType / layer-metadata requests on load.
 *
 * The cache stores the in-flight PROMISE, so simultaneous callers share one
 * request. Entries are dropped on failure so a transient outage does not
 * poison the cache for the rest of the session.
 */

import { getDataSourceAdapter } from './dashboardDataService';
import type { DashboardDataSource, DashboardField } from '../types/dashboardTypes';

const cache = new Map<string, Promise<DashboardField[]>>();

function cacheKey(source: DashboardDataSource): string {
  /* A local source has no URL or type name to be identified by — two
     shapefiles imported into the same dashboard would share the key and the
     second would show the first one's columns. Its own id is the only thing
     that distinguishes it. */
  if (source.serviceType === 'local') return `local|${source.id}`;
  return `${source.serviceType}|${source.serviceUrl}|${source.layerName}|${source.arcgisLayerId ?? ''}`;
}

export function fetchFieldsCached(source: DashboardDataSource): Promise<DashboardField[]> {
  const key = cacheKey(source);
  const existing = cache.get(key);
  if (existing) return existing;

  const request = getDataSourceAdapter(source)
    .fetchFields(source)
    .catch((error: unknown) => {
      cache.delete(key);
      throw error;
    });

  cache.set(key, request);
  return request;
}

/** Drops cached fields — used after a manual refresh so schema changes appear. */
export function invalidateFields(source?: DashboardDataSource): void {
  if (source) cache.delete(cacheKey(source));
  else cache.clear();
}
